<template>
  <el-container v-if="!showInstructions">
    <el-header v-if="project">
      <h2>{{ project.name }}</h2>
      <el-form :inline="true" :model="filters">
        <i class="fas el-icon-fa-filter icon-filter" />
        <el-form-item>
          <el-popover
            title="Filter by date"
            trigger="hover"
            content="Selecting a start date and/or an end date. You can narrow down the payloads you want to browse."
          >
            <el-date-picker
              slot="reference"
              v-model="filters.dates"
              type="datetimerange"
              range-separator="-"
              start-placeholder="Start date"
              end-placeholder="End date"
              prefix-icon="fas el-icon-fa-calendar"
              align="right"
              size="mini"
              clearable
            />
          </el-popover>
        </el-form-item>
        <el-form-item>
          <el-popover
            title="Filter by user identifier"
            trigger="hover"
            content="Type here the user identifier of the user's payloads you want to browse."
          >
            <el-input
              slot="reference"
              v-model="filters.userIdentifier"
              placeholder="User identifier"
              prefix-icon="fas el-icon-fa-user"
              size="mini"
              clearable
            />
          </el-popover>
        </el-form-item>
        <el-form-item>
          <el-popover
            title="Filter by device identifier"
            trigger="hover"
            content="Type here the device identifier of the user's device payloads you want to browse."
          >
            <el-input
              slot="reference"
              v-model="filters.deviceIdentifier"
              placeholder="Device identifier"
              prefix-icon="fas el-icon-fa-mobile-alt"
              size="mini"
              clearable
            />
          </el-popover>
        </el-form-item>
      </el-form>
      <span>
        <el-button
          type="primary"
          :icon="isPaused ? 'fas el-icon-fa-play' : 'fas el-icon-fa-pause'"
          @click="playbackClicked"
          size="mini"
        />
        <el-button type="primary" icon="fas el-icon-fa-redo" @click="refreshClicked" size="mini" />
        <el-button type="danger" @click="deleteRecords" size="mini">
          Delete records
          <i class="el-icon-delete el-icon-right"></i>
        </el-button>
      </span>
    </el-header>
    <transition name="el-fade-in">
      <el-main v-loading="isLoading">
        <el-tree
          :data="records"
          node-key="id"
          default-expand-all
          :expand-on-click-node="false"
          icon-class="fas el-icon-fa-caret-right"
          @node-click="nodeSelected"
          @node-contextmenu="nodeRightClicked"
          empty-text="No data. It may be due to too restrictive filters."
        >
          <HttpRecordCell :record="node.data" slot-scope="{ node, }" />
        </el-tree>
      </el-main>
    </transition>
    <el-footer>
      <el-pagination
        @current-change="pageChanged"
        :current-page="currentPage"
        layout="prev, pager, next"
        :total="1000"/>
    </el-footer>
  </el-container>
  <div class="empty-container" v-else>
    <h1>Your project is empty. Follow instructions to start recording HTTP payloads.</h1>
    <SdksInstructions :api_key="api_key" :isPublic="false" />
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";

import HttpRecordCell from "@/components/HttpRecordCell.vue";
import SdksInstructions from "@/components/SdksInstructions.vue";

export default {
  components: {
    HttpRecordCell,
    SdksInstructions
  },
  data: function() {
    return {
      project: {},
      records: [],
      filters: {
        userIdentifier: null,
        deviceIdentifier: null,
        dates: null,
      },
      currentPage: 1,
      pauseDate: Date(),
      fecthTimeout: null,
      isLoading: true
    };
  },
  created: function() {
    this.fetchProject();
    this.fetchRecords();
  },
  watch: {
    $route(to, from) {
      this.fetchProject();
      this.fetchRecords();
    },
    filters: {
      handler: function() {
        // you need function for "this" to be defined
        this.fetchRecords();
      },
      deep: true
    }
  },
  methods: {
    fetchProject() {
      axios({
        method: "GET",
        url: "/api/v1/project/" + this.api_key + "/",
      }).then(resp => {
        this.project = resp.data;
      });
    },
    fetchRecords() {
      this.isLoading = true && this.isPaused; // We don't want to show loading during live stream
      clearTimeout(this.fecthTimeout);
      if (this.api_key) {
        console.debug("fetching records... " + moment().format("LTS"));
        let url = "/api/v1/project/" + this.api_key + "/http_record/";
        let [startDate, endDate] = this.filters.dates || [null, null];
        let params = {
          datetime_after: startDate ? moment(startDate).toISOString() : null,
          datetime_before: endDate ? moment(endDate).toISOString() : null,
          user_identifier: this.filters.userIdentifier,
          device_identifier: this.filters.deviceIdentifier,
          offset: Math.max(0, 50 * (this.currentPage - 1)),
        };
        console.debug(params);
        const tthis = this;
        axios({ url: url, params: params, method: "GET" })
          .then(function(resp) {
            tthis.records = resp.data.results;
          })
          .catch(function(err) {
            console.error(err);
          })
          .finally(function() {
            tthis.isLoading = false;
            clearTimeout(tthis.fecthTimeout); // more secure (avoid threading issue leading to multiple timers)
            if (!tthis.pauseDate) {
              tthis.fecthTimeout = setTimeout(
                tthis.fetchRecords.bind(tthis),
                3000
              );
            }
          });
      }
    },
    pageChanged(page) {
      this.currentPage = page;
      this.fetchRecords();
    },
    deleteRecords() {
      if (this.$store.state.auth.username == "demo") {
        this.$message.error("Feature disabled for demo account");
      } else {
        this.$confirm(
          "This will permanently delete all records of this project, including the one not matching current filters. Continue?",
          "Warning",
          {
            confirmButtonText: "DELETE",
            cancelButtonText: "Cancel",
            type: "warning",
            center: true
          }
        )
          .then(() => {
            axios({
              url: "/api/v1/project/" + this.api_key + "/clear/",
              method: "POST"
            }).then(resp => {
              this.records = [];
              this.$message({
                type: "success",
                message: "Delete completed"
              });
            });
          })
          .catch(() => {
            this.$message({
              type: "info",
              message: "Delete canceled"
            });
          });
      }
    },
    playbackClicked() {
      this.pauseDate = this.pauseDate ? null : Date();
      this.fetchRecords();
    },
    refreshClicked() {
      this.fetchRecords();
    },
    nodeSelected(item) {
      this.$router.push({
        name: "record",
        params: {
          api_key: this.api_key,
          record_id: item.id
        }
      });
    },
    nodeRightClicked(event, item) {
      const routeData = this.$router.resolve({
        name: "record",
        params: {
          api_key: this.api_key,
          record_id: item.id
        }
      });
      window.open(routeData.href, '_blank');
    }
  },
  computed: {
    isPaused() {
      return this.pauseDate != null;
    },
    showInstructions() {
      const filtersActive =
        this.filters.userIdentifier ||
        this.filters.deviceIdentifier ||
        this.filters.dates;

      if (this.isLoading) {
        return false;
      }
      if (!this.project) {
        return false;
      }
      if (!this.records.length && !filtersActive) {
        return true;
      }
      return false;
    },
    api_key() {
      return this.$route.params.api_key;
    }
  }
};
</script>

<style scoped lang="sass">

h2
  color: $--color-text-secondary

.icon-filter
  margin-right: 10px
  color: $--color-text-secondary

// Layout form to be horizontal
.el-form
  @include flex($h: center, $v: center)

  &--inline    // inline is a hack to be prioritary
  .el-form-item
    display: flex
    margin: auto

    &:not(:last-child)
      margin-right: 5px

.el-footer
  @include flex($h: center, $v: center)

// Boldify line on hover and selection
.is-current .uri
  text-decoration: underline
  font-weight: bold

.empty-container
  @include flex($h: center, $v: center)
  flex-direction: column
  height: 100%
  margin: 50px

</style>

